import {
  useCallback,
  useRef,
  useState,
  type DragEvent,
  type ChangeEvent,
} from 'react';
import {
  Camera,
  CheckCircle2,
  Loader2,
  Paperclip,
  RotateCcw,
  Upload,
  X,
} from 'lucide-react';
import {
  COMPARATEUR_ACCEPTED_BILL_TYPES,
  COMPARATEUR_MAX_BILL_SIZE_BYTES,
  type BillSeason,
} from '@shared/schemas/comparateur';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';

const ACCEPTED_LABEL = 'PDF, JPG, PNG, WEBP, HEIC';
const ACCEPT_ATTRIBUTE = COMPARATEUR_ACCEPTED_BILL_TYPES.join(',');

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

export interface DropzoneUploadedFile {
  key: string;
  filename: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

export interface FileDropzoneProps {
  season: BillSeason;
  label: string;
  helperText?: string;
  uploaded: DropzoneUploadedFile | null;
  onUpload: (file: File) => Promise<void>;
  onClear?: () => void;
  disabled?: boolean;
}

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; progress: number; filename: string }
  | { kind: 'error'; message: string };

export function FileDropzone({
  season,
  label,
  helperText,
  uploaded,
  onUpload,
  onClear,
  disabled,
}: FileDropzoneProps) {
  const [state, setState] = useState<UploadState>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (disabled) return;
      if (file.size > COMPARATEUR_MAX_BILL_SIZE_BYTES) {
        setState({
          kind: 'error',
          message: `Fichier trop volumineux (max ${formatBytes(COMPARATEUR_MAX_BILL_SIZE_BYTES)}).`,
        });
        return;
      }
      if (
        !(COMPARATEUR_ACCEPTED_BILL_TYPES as readonly string[]).includes(file.type)
      ) {
        setState({
          kind: 'error',
          message: `Format non supporté. Acceptés : ${ACCEPTED_LABEL}.`,
        });
        return;
      }
      setState({ kind: 'uploading', progress: 0, filename: file.name });
      try {
        await onUpload(file);
        setState({ kind: 'idle' });
      } catch (err) {
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : "Erreur lors de l'envoi.",
        });
      }
    },
    [disabled, onUpload],
  );

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function onPicked(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  }

  function clearUploaded(): void {
    setState({ kind: 'idle' });
    onClear?.();
  }

  const uploading = state.kind === 'uploading';
  const errorMsg = state.kind === 'error' ? state.message : null;

  if (uploaded) {
    return (
      <div className="rounded-xl border-2 border-brand-200 bg-brand-50/40 p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100">
            <CheckCircle2 className="h-5 w-5 text-brand-700" strokeWidth={1.7} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-700">
              Facture {season === 'summer' ? 'été' : 'hiver'} reçue
            </p>
            <p className="mt-1 truncate text-[14px] font-semibold text-ink">
              {uploaded.filename}
            </p>
            <p className="mt-0.5 text-[12px] text-stone-500">
              {formatBytes(uploaded.size)} · {uploaded.contentType.split('/').pop()?.toUpperCase()}
            </p>
          </div>
          {onClear && (
            <button
              type="button"
              onClick={clearUploaded}
              disabled={disabled}
              className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-white hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              aria-label="Remplacer le fichier"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={cn(
          'relative cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
          dragOver
            ? 'border-brand-400 bg-brand-50/70'
            : 'border-stone-300 bg-stone-50/60 hover:border-stone-400 hover:bg-stone-50',
          disabled && 'cursor-not-allowed opacity-60',
          errorMsg && 'border-red-300 bg-red-50/60',
          uploading && 'border-brand-400 bg-brand-50/40',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          onChange={onPicked}
          className="hidden"
          tabIndex={-1}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPicked}
          className="hidden"
          tabIndex={-1}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
            <p className="text-[13px] font-semibold text-brand-800">
              Envoi en cours…
            </p>
            <p className="max-w-[260px] truncate text-[11px] text-stone-500">
              {state.filename}
            </p>
          </div>
        ) : (
          <>
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-[0_1px_3px_rgba(0,0,0,.06)]">
              <Upload className="h-4 w-4 text-stone-500" strokeWidth={1.8} />
            </div>
            <p className="mt-3 text-[14px] font-semibold text-ink">{label}</p>
            <p className="mt-1 text-[12px] text-stone-500">
              Glissez-déposez votre facture ou cliquez pour parcourir
            </p>
            <p className="mt-1 text-[11px] text-stone-400">
              {ACCEPTED_LABEL} · max {formatBytes(COMPARATEUR_MAX_BILL_SIZE_BYTES)}
            </p>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                className="gap-1.5"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Choisir un fichier
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  cameraInputRef.current?.click();
                }}
                className="gap-1.5"
              >
                <Camera className="h-3.5 w-3.5" />
                Prendre une photo
              </Button>
            </div>

            {helperText && (
              <p className="mt-3 text-[11px] text-stone-400">{helperText}</p>
            )}
          </>
        )}
      </div>

      {errorMsg && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">{errorMsg}</span>
          <button
            type="button"
            onClick={() => setState({ kind: 'idle' })}
            className="text-red-600 underline-offset-2 hover:underline"
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
}
